'use client';
import * as Styled from './styled';
import { RenderHeaderCellProps } from 'react-data-grid';
import { ColumnWithDType } from '../../types';
import { TRenderAsEnum } from '../../DataGrid/lib';
import MaybeEvalStatus from '../MaybeEvalStatus';
import Editor from '@monaco-editor/react';

const THEME = 'chrome-devtools';

const editorOptions = {
  lineNumbers: false,
  minimap: { enabled: false },
  // wordWrap: 'on',
  fontFamily: 'Menlo, Monaco, "Courier New", monospace',
  fontLigatures: true,
  fontSize: 12,
  fontWeight: 'normal',
  fontVariations: false,
  scrollBeyondLastLine: false,
  autoLayout: true,
  padding: { top: 10, bottom: 0, left: 0, right: 0 },
};

const DetailsEditor: React.FC<{ value: any; onChange: (value: string) => void; language?: string }> = ({
  value,
  onChange,
  language,
}) => {
  const numLines = value.split('\n').length || 1;
  return (
    <Editor
      height={`${numLines + 2}lh`}
      defaultLanguage={language}
      language={language}
      theme={THEME}
      defaultValue={value}
      options={editorOptions}
      onChange={(value) => onChange(value as string)}
      width="100%"
    />
  );
};

export const Details: React.FC<
  React.HTMLAttributes<HTMLDivElement> & {
    field: ColumnWithDType;
    value: string;
    language?: string;
    rootPath?: never;
    onChange?: (value: string | null) => void;
  }
> = ({ field, value, language, rootPath, onChange, ...rest }) => {
  const numLines = value.split('\n').length || 1;
  const editorHeight = `${numLines > 20 ? 22 : numLines + 2}lh`;

  return onChange ? (
    <DetailsEditor value={value} onChange={onChange} language={language} />
  ) : (
    <Styled.Details {...rest}>
      <Editor
        height={editorHeight}
        language={language}
        theme={THEME}
        defaultValue={value}
        options={{ ...editorOptions, readOnly: true }}
        width="100%"
      />
    </Styled.Details>
  );
};

export const Cell: React.FC<
  React.HTMLAttributes<HTMLDivElement> & { field: ColumnWithDType; value: string; renderAs: TRenderAsEnum }
> = ({ field, value, renderAs, ...rest }) => {
  return (
    <Styled.Cell {...rest}>
      <MaybeEvalStatus value={value}>
        <span>{value?.toString().trim()}</span>
      </MaybeEvalStatus>
    </Styled.Cell>
  );
};

export const Header: React.FC<RenderHeaderCellProps<any> & { children: React.ReactNode }> = ({ children, ...rest }) => {
  const field = rest.column as ColumnWithDType;
  const displayName = field.name === '_oxen_row_id' ? '(idx)' : field.name;
  return (
    <Styled.Header>
      <div className="header-cell-name">{displayName}</div>
      {children}
    </Styled.Header>
  );
};
